export const IS_DEV = false;

export const API_BASE_URL = IS_DEV? "http://localhost:8000": "https://api.beatai.io";

// Network error callback type
let networkErrorCallback = null;
// Unauthorized error callback type
let unauthorizedErrorCallback = null;
// Technical error callback type
let technicalErrorCallback = null;

// Set error handling callbacks
export const setErrorCallbacks = ({
  onNetworkError = null,
  onUnauthorized = null,
  onTechnicalError = null
}) => {
  networkErrorCallback = onNetworkError;
  unauthorizedErrorCallback = onUnauthorized;
  technicalErrorCallback = onTechnicalError;
};

setErrorCallbacks({
  onNetworkError: (response) => {
    console.error('Network error:', response);
  },
  
  // Mandatory: Handle unauthorized access
  onUnauthorized: (response) => {
    console.error('Unauthorized access');
    if (window.location.href != "/login")
      logoutUser();
  },
  
  onTechnicalError: (response) => {
    window.location.href = "/technical-error"
  }
});

export const logoutUser = () => {
  // Clear tokens
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("verified");
  localStorage.removeItem("model");

  // Redirect to login with special code
  window.location.href = "/login?sc=ef5199c2-e6b8-4257-9932-20aa6f449c90R&reason=session-exp";
};

export const fetchWithErrorHandling = async (
  url, 
  options = {}, 
  skipAuthCheck = false
) => {
  // Prepare default headers
  const headers = {
    ...options.headers,
    "Content-Type": "application/json",
  };

  // Add Authorization header if not skipped and token exists
  if (!skipAuthCheck) {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
  }

  try {
    // Perform fetch with merged options
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Handle different error scenarios
    if (!response.ok) {
      // Unauthorized error (401 or 403)
      if (response.status === 401 || response.status === 403) {
        // Call mandatory unauthorized callback
        if (unauthorizedErrorCallback) {
          unauthorizedErrorCallback(response);
        } else {
          // Default behavior: logout user
          logoutUser();
        }
        throw { response: { data: "Unauthorized access" } };
      }

      if (response.status === 404) throw new Error("not_found")

      // Network or technical error handling
      if (response.status >= 500) {
        // Optional network error callback
        if (networkErrorCallback) {
          networkErrorCallback(response);
        }
        throw { 
          response: { 
            data: "Network or server error occurred" 
          } 
        };
      }

      // Technical error handling
      if (technicalErrorCallback) {
        technicalErrorCallback(response);
      }

      // If response is not ok, try to parse error details
      const errorData = await response.json().catch(() => ({}));
      throw { response: { data: errorData || "Technical error occurred" } };
    }

    // Return parsed JSON for successful responses
    return await response.json();
  } catch (error) {
    // Rethrow error for specific error handling in individual API calls
    throw error;
  }
};

// Update existing API calls to use the new fetchWithErrorHandling wrapper
export const getProfile = async () => {
  return fetchWithErrorHandling(`${API_BASE_URL}/get_profile/`, {
    method: "GET"
  });
};

export const autoSuggest = async (query) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/auto_suggest/`, {
    method: "POST",
    body: JSON.stringify({ query })
  });
};

export const signupUser = async (userData) => {
  return fetch(`${API_BASE_URL}/signup/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(userData)
  }, true);  // Skip auth check for signup
};

export const loginUser = async (credentials) => {
  return fetch(`${API_BASE_URL}/login/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(credentials)
  }, true).then(async (response) => {
    const data = await response.json();
    console.log("D", data)
    if ("error" in data) throw data;
    if (!response.ok) throw new Error("Technical error!");

    if (data) {
      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      localStorage.setItem("verified", data.verified);

      return data
    }
    
  }).catch((e) => {
    throw e
  });
};

export const requestPasswordReset = async (email, CFToken) => {
  return fetch(`${API_BASE_URL}/password-reset/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ email, captcha: CFToken })
  });  // Skip auth check for password reset
};

export const confirmPasswordReset = async (token, newPassword, captcha) => {
  return fetch(
    `${API_BASE_URL}/password-reset-confirm/${token}/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ 
        captcha,
        new_password: newPassword, 
        token: token 
      })
    },
  );
};

export const resendVerification = async () => {
  return fetchWithErrorHandling(`${API_BASE_URL}/resend-verification/`, {
    method: "POST"
  });
};

export const verifyEmail = async (token) => {
  return fetchWithErrorHandling(
    `${API_BASE_URL}/verify-email?token=${token}`, 
    { method: "GET" },
    true  // Skip auth check for email verification
  );
};

export const getChats = async () => {
  return fetchWithErrorHandling(`${API_BASE_URL}/get_user_chat_history/`);
};

export const getChatMessages = async (chatId) => {
  const chat = await fetchWithErrorHandling(`${API_BASE_URL}/chats/${chatId}/`);
  return chat.messages;
};

export const createChat = async (chatData) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/chats/`, {
    method: "POST",
    body: JSON.stringify(chatData)
  });
};

export const addMessageToChat = async (chatId, messageData) => {
  return fetchWithErrorHandling(
    `${API_BASE_URL}/chats/${chatId}/add_message/`, 
    {
      method: "POST",
      body: JSON.stringify(messageData)
    }
  );
};

export const addAttachementToChat = async (fileObject, messageId) => {
  const formData = new FormData();
  formData.append('file', fileObject);
  formData.append('message_id', messageId);

  return fetch(`${API_BASE_URL}/create_attachment/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    },
    body: formData
  });
};

export const deleteChat = async (chatId,) => {
  return fetch(
    `${API_BASE_URL}/chats/${chatId}/`, 
    {
      method: "DELETE",
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      }
    }
  );
};

export const generateImage = async (
  promptText,
  fileRef,
  modelName = "default"
) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/ai-image/`, {
    method: "POST",
    body: JSON.stringify({
      prompt_txt: promptText,
      model_name: modelName
    })
  });
};

export const deleteImage = async (
  fileRef,
) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/ai-image/?file_ref=${fileRef}`, {
    method: "DELETE",
  });
};

export const getGeneratedImages = async () => {
  return fetchWithErrorHandling(`${API_BASE_URL}/ai-image/`, {
    method: "GET"
  });
};

export const getDefaultEvents = async (date) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/events/default/?date=${date}`, {
    method: "GET",
    headers: {
      "Accept": "*/*"
    }
  });
};

export const changeAvatar = async (file) => {
  const formData = new FormData();
  formData.append("avatar", file);

  const response = await fetch(API_BASE_URL + "/change_avatar/", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to update avatar");
  }

  return response.json();
};

export const getAvatarBase64 = async () => {
  const response = await fetch(API_BASE_URL + "/get_avatar/", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to fetch avatar");
  }

  const data = await response.json();
  return data.avatar;
};

export const searchChats = async (q) => {
  const response = await fetch(API_BASE_URL + "/chats/search/?q=" + q, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to fetch avatar");
  }

  const data = await response.json();
  return data;
};

export const updateProfile = async (profileData) => {
  const response = await fetch(API_BASE_URL + "/profile/update/", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: JSON.stringify(profileData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData);
  }

  return response.json();
};

export const changePassword = async (passwordData) => {
  const response = await fetch(API_BASE_URL + "/password/change/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: JSON.stringify(passwordData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "Failed to change password");
  }

  return response.json();
};

export const requestEmailChange = async (newEmail) => {
  const response = await fetch(API_BASE_URL + "/email/change/request/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: JSON.stringify({ new_email: newEmail }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to request email change");
  }

  return response.json();
};

export const validateEmailChange = async (otp) => {
  const response = await fetch(API_BASE_URL + "/email/change/validate/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
    body: JSON.stringify({ otp: otp }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to validate email change");
  }

  return response.json();
};

// Function to get presigned URL and download the file
export const downloadAttachment = async (attachmentId) => {
  try {
    // First, get the presigned URL
    const response = await fetch(API_BASE_URL + '/attachment_presigned_download/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`

      },
      body: JSON.stringify({ id: attachmentId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = data.url;
    link.download = data.filename; // Use the original filename

    // Append to document, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
}

// Subscription-related API functions
export const activateSubscription = async (planId) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/api/demo_activate_subs/`, {
    method: "POST",
    body: JSON.stringify({ plan_id: planId })
  });
};

export const cancelSubscription = async () => {
  return fetchWithErrorHandling(`${API_BASE_URL}/api/demo_cancel_subs/`, {
    method: "POST"
  });
};

export const topUpCredits = async (credits, amountUsd) => {
  return fetchWithErrorHandling(`${API_BASE_URL}/api/demo_topup_credits/`, {
    method: "POST",
    body: JSON.stringify({ 
      credits: credits,
      amount_usd: amountUsd
    })
  });
};
